import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Eye, Archive, Trash2, BookOpen, MoreVertical, RotateCcw } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loading, EmptyState } from "@/components/common/Loading";
import { SeashellRating } from "@/components/common/Seashell";
import { labelFor, useReferences } from "@/hooks/useReferences";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, runBulk } from "@/components/common/BulkActionBar";

const SORTS = [
  { value: "recent", label: "Recently updated" },
  { value: "title_asc", label: "Title A–Z" },
  { value: "title_desc", label: "Title Z–A" },
  { value: "status", label: "Status" },
];

export default function MyResources() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { references } = useReferences();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [sort, setSort] = useState("recent");
  const [bulkDel, setBulkDel] = useState(false);

  const load = async () => { const { data } = await api.get("/resources/mine"); setItems(data); };
  useEffect(() => { load(); }, []);

  // Auto-open the create dialog when arriving from a directory "Create" CTA (?new=1).
  useEffect(() => {
    if (searchParams.get("new")) {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    const t = (x) => (x.title || "").toLowerCase();
    if (sort === "title_asc") arr.sort((a, b) => t(a).localeCompare(t(b)));
    else if (sort === "title_desc") arr.sort((a, b) => t(b).localeCompare(t(a)));
    else if (sort === "status") arr.sort((a, b) => (a.status || "").localeCompare(b.status || "") || t(a).localeCompare(t(b)));
    else arr.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return arr;
  }, [items, sort]);

  const ids = useMemo(() => sorted.map((i) => i.id), [sorted]);
  const sel = useRowSelection(ids);

  // Groups (GRP-DEC-315) — same contract as Events.
  const fromGroup = React.useRef(searchParams.get("group"));

  const create = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/resources", {
        title: name.trim(),
        ...(fromGroup.current ? { group_id: fromGroup.current } : {}),
      });
      setOpen(false); setName("");
      navigate(`/dashboard/resources/${data.id}${fromGroup.current
        ? `?group=${fromGroup.current}` : ""}`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create."); }
  };
  const publish = async (it, v) => {
    try { await api.post(`/resources/${it.id}/publish?published=${v}`); toast.success(v ? "Published" : "Unpublished"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const archive = async (it, v) => { await api.post(`/resources/${it.id}/archive?archived=${v}`); toast.success(v ? "Archived" : "Restored"); load(); };
  const remove = async (it) => { await api.delete(`/resources/${it.id}`); toast.success("Deleted"); load(); };

  const bulkPublish = async (v) => { await runBulk(sel.selected, (id) => api.post(`/resources/${id}/publish?published=${v}`), v ? "Published" : "Unpublished"); sel.clear(); load(); };
  const bulkRemove = async () => { await runBulk(sel.selected, (id) => api.delete(`/resources/${id}`), "Deleted"); sel.clear(); setBulkDel(false); load(); };

  if (!items) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">My Resources</h1>
          <p className="mt-1 text-muted-foreground">Share books, tools, courses and more with the community.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="resource-create-button"><Plus className="mr-2 h-4 w-4" /> New resource</Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-8"><EmptyState icon={BookOpen} title="No resources yet" description="Add your first resource to help fellow founders." action={<Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Create one</Button>} testId="resource-empty" /></div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} data-testid="mine-select-all" /> Select all
            </label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48" data-testid="mine-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((it) => (
              <Card key={it.id} className={`flex flex-col p-5 ${sel.isSelected(it.id) ? "ring-2 ring-primary" : ""}`} data-testid={`resource-card-${it.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Checkbox className="mt-1" checked={sel.isSelected(it.id)} onCheckedChange={() => sel.toggle(it.id)} data-testid={`select-resource-${it.id}`} />
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-secondary">
                      {it.cover_url ? <img src={fileUrl(it.cover_url)} alt="" className="h-full w-full object-cover" /> : <BookOpen className="h-6 w-6 text-muted-foreground" />}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="rounded-md p-1.5 hover:bg-secondary" data-testid={`resource-menu-${it.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/dashboard/resources/${it.id}`)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                      {it.published && it.slug && <DropdownMenuItem onClick={() => window.open(`/resource/${it.slug}`, "_blank")}><Eye className="mr-2 h-4 w-4" /> View public</DropdownMenuItem>}
                      {it.status === "archived"
                        ? <DropdownMenuItem onClick={() => archive(it, false)}><RotateCcw className="mr-2 h-4 w-4" /> Restore</DropdownMenuItem>
                        : <DropdownMenuItem onClick={() => archive(it, true)}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="mt-3 font-[640] text-foreground">{it.title}</h3>
                {it.resource_type && <p className="mt-0.5 text-sm text-muted-foreground">{labelFor(references.resource_types, it.resource_type)}</p>}
                <div className="mt-2"><SeashellRating value={it.rating_avg} readOnly size="h-4 w-4" showValue count={it.rating_count} /></div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {it.status === "archived" ? <Badge variant="secondary">Archived</Badge> : it.published ? <Badge className="bg-teal-50 text-teal-700">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                  <Badge variant="secondary" className="capitalize">{it.source}</Badge>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="secondary" className="flex-1" onClick={() => navigate(`/dashboard/resources/${it.id}`)} data-testid={`resource-edit-${it.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                  {it.status !== "archived" && <Button size="sm" variant={it.published ? "ghost" : "default"} onClick={() => publish(it, !it.published)} data-testid={`resource-publish-${it.id}`}>{it.published ? "Unpublish" : "Publish"}</Button>}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid={`resource-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete {it.title}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(it)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <BulkActionBar count={sel.count} onClear={sel.clear}>
        <Button size="sm" variant="secondary" onClick={() => bulkPublish(true)} data-testid="bulk-publish"><Eye className="mr-1.5 h-4 w-4" /> Publish</Button>
        <Button size="sm" variant="secondary" onClick={() => bulkPublish(false)} data-testid="bulk-unpublish">Unpublish</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkDel(true)} data-testid="bulk-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
      </BulkActionBar>

      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {sel.count} resource{sel.count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="bulk-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New resource</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Resource title</Label><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. The Mom Test" data-testid="resource-create-name" /></div>
          <DialogFooter><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="resource-create-confirm">Create &amp; edit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
